<div class="modal-header">
    <h4 class="modal-title">{{ title }}</h4>
</div>

@if (isAdding) {
    <div class="wizard-steps">
        <div class="wizard-step" [class.active]="currentStep === 1" [class.completed]="currentStep > 1">
            <div class="wizard-step-circle">
                @if (currentStep > 1) {
                    <fa-icon [icon]="checkIcon"></fa-icon>
                } @else {
                    1
                }
            </div>
            <div class="wizard-step-label">Record</div>
        </div>
        <div class="wizard-step-connector" [class.completed]="currentStep > 1"></div>
        <div class="wizard-step" [class.active]="currentStep === 2" [class.completed]="currentStep > 2">
            <div class="wizard-step-circle">
                @if (currentStep > 2) {
                    <fa-icon [icon]="checkIcon"></fa-icon>
                } @else {
                    2
                }
            </div>
            <div class="wizard-step-label">Review</div>
        </div>
        <div class="wizard-step-connector" [class.completed]="currentStep > 2"></div>
        <div class="wizard-step" [class.active]="currentStep === 3" [class.completed]="false">
            <div class="wizard-step-circle">3</div>
            <div class="wizard-step-label">Confirm</div>
        </div>
    </div>
}

@if (isAdding && currentStep === 1) {
    <div class="modal-body">
        <div class="step-card">
            <div class="step-card-header">
                <div class="step-card-header-icon icon-record">
                    <fa-icon [icon]="micIcon"></fa-icon>
                </div>
                <div class="step-card-header-text">
                    <div class="step-card-title">Record Dictation</div>
                    <div class="step-card-subtitle">Describe the pending change order in your own words</div>
                </div>
            </div>

            <div class="dictation-tips">
                <div class="dictation-tips-title">Tips for a complete record</div>
                <ul>
                    <li>State the <strong>job number</strong></li>
                    <li>Say who <strong>requested or initiated</strong> the change — one of: <strong>Owner</strong>, <strong>Engineer</strong>, or <strong>Vancon</strong></li>
                    <li>Provide a <strong>description of the change order</strong> — what work is being changed and why</li>
                    <li>Include any <strong>additional notes</strong> — urgency, coordination details, special instructions, etc.</li>
                </ul>
            </div>

            <span class="form-label fw-semibold d-block" style="font-size: 13px">Transcription</span>
            <app-voice-text-field
                name="pocoWizardTranscription"
                [(ngModel)]="finalText"
                [rows]="10"
                [autoStart]="voiceAutoStart"
                placeholder="Speak or type your notes here — transcription builds as you dictate."
            ></app-voice-text-field>
        </div>
    </div>
    <div class="modal-footer">
        <div class="wizard-footer">
            <div></div>
            <div class="wizard-footer-end">
                <button class="btn btn-secondary" (click)="cancel()">Cancel</button>
                <button class="btn btn-primary" (click)="goToStep(2)" [disabled]="!finalText">
                    Next: Review <fa-icon [icon]="arrowRightIcon" class="ms-1"></fa-icon>
                </button>
            </div>
        </div>
    </div>
}

@if (isAdding && currentStep === 2) {
    <div class="modal-body">
        <div class="step-card">
            @if (!isExtracting) {
                <div class="step-card-header">
                    <div class="step-card-header-icon icon-edit">
                        <fa-icon [icon]="editIcon"></fa-icon>
                    </div>
                    <div class="step-card-header-text">
                        <div class="step-card-title">Review Transcription</div>
                        <div class="step-card-subtitle">Edit the text if needed, then send to AI for field extraction</div>
                    </div>
                </div>

                <textarea
                    class="form-control"
                    [(ngModel)]="finalText"
                    name="transcriptionEdit"
                    id="transcriptionEdit"
                    rows="10"
                    placeholder="Edit the transcription text..."
                    style="border-radius: 10px; font-size: 14px"
                ></textarea>

                @if (extractionError) {
                    <div class="alert alert-danger mt-3 mb-0"><strong>Extraction failed:</strong> {{ extractionError }}</div>
                }
            } @else {
                <div class="ai-loading-container">
                    <div
                        class="step-card-header-icon icon-ai"
                        style="width: 56px; height: 56px; min-width: 56px; border-radius: 50%; font-size: 24px"
                    >
                        <fa-icon [icon]="faSpinner" [animation]="'spin'"></fa-icon>
                    </div>
                    <div class="ai-loading-text">Extracting fields...</div>
                    <div class="ai-loading-subtext">This usually takes a few seconds</div>
                </div>
            }
        </div>
    </div>
    <div class="modal-footer">
        <div class="wizard-footer">
            <button class="btn btn-outline-secondary" (click)="goToStep(1)" [disabled]="isExtracting">
                <fa-icon [icon]="arrowLeftIcon" class="me-1"></fa-icon> Back
            </button>
            <div class="wizard-footer-end">
                <button class="btn btn-secondary" (click)="cancel()" [disabled]="isExtracting">Cancel</button>
                <button class="btn btn-primary" (click)="sendToAi()" [disabled]="!finalText || isExtracting">
                    @if (isExtracting) {
                        <fa-icon [icon]="faSpinner" [animation]="'spin'" class="me-1"></fa-icon> Extracting...
                    } @else if (extractionError) {
                        <fa-icon [icon]="wandIcon" class="me-1"></fa-icon> Retry
                    } @else {
                        <fa-icon [icon]="wandIcon" class="me-1"></fa-icon> Send to AI
                    }
                </button>
            </div>
        </div>
    </div>
}

@if (currentStep === 3) {
    <form name="form" #form="ngForm">
        <div class="modal-body review-form">
            <div class="form-section-title">Change Order Details</div>
            <div class="row mb-3">
                <div class="col-md-6 form-group">
                    <label for="job">Job</label>
                    <wj-combo-box
                        [(ngModel)]="target.job"
                        name="job"
                        id="job"
                        [itemsSource]="jobOptions"
                        [displayMemberPath]="'display'"
                        [selectedValuePath]="'value'"
                    ></wj-combo-box>
                </div>
                <div class="col-md-6 form-group">
                    <label for="initiatedBy">Requested/Initiated By</label>
                    <wj-combo-box
                        [(ngModel)]="target.initiatedBy"
                        name="initiatedBy"
                        id="initiatedBy"
                        [isRequired]="true"
                        [itemsSource]="initiatedByOptions"
                        [displayMemberPath]="'display'"
                        [selectedValuePath]="'value'"
                    ></wj-combo-box>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 form-group">
                    <label for="description">Description</label>
                    <textarea
                        class="form-control"
                        [(ngModel)]="target.description"
                        name="description"
                        id="description"
                        rows="4"
                    ></textarea>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 form-group">
                    <label for="notes">Additional Notes</label>
                    <textarea
                        class="form-control"
                        [(ngModel)]="target.notes"
                        name="notes"
                        id="notes"
                        rows="3"
                    ></textarea>
                </div>
            </div>

            @if (target.rawTranscription) {
                <details class="mb-3">
                    <summary class="text-muted" style="cursor: pointer; font-size: 13px">View Raw Transcription</summary>
                    <div
                        class="form-control mt-2"
                        style="min-height: 60px; background: var(--bs-body-bg); white-space: pre-wrap; border-radius: 10px"
                    >
                        {{ target.rawTranscription }}
                    </div>
                </details>
            }
        </div>
        <div class="modal-footer">
            <div class="wizard-footer">
                <div>
                    @if (isAdding) {
                        <button class="btn btn-outline-secondary" (click)="goToStep(2)">
                            <fa-icon [icon]="arrowLeftIcon" class="me-1"></fa-icon> Back to Review
                        </button>
                    }
                </div>
                <div class="wizard-footer-end">
                    <button id="btnSave" type="submit" class="btn btn-primary" (click)="save()" [disabled]="form.invalid || isSaving">
                        @if (isSaving) {
                            <span><fa-icon [icon]="faSpinner" [animation]="'spin'"></fa-icon>&nbsp;Saving</span>
                        } @else {
                            <span>Save</span>
                        }
                    </button>
                    <button id="btnCancel" type="button" class="btn btn-secondary" (click)="cancel()" [disabled]="isSaving">Cancel</button>
                </div>
            </div>
        </div>
    </form>
}
